import { createActor } from "xstate";

import { appBus } from "@src/features/events";
import { patientStore } from "@src/features/patients";
import { events } from "@src/shared/utils/observability/events";
import { PositiveInt } from "@src/shared/utils/types";

import {
  encounterMachine as baseEncounterMachine,
  type EncounterEvent,
} from "./encounter.machine";

export const encounterActor = createActor(baseEncounterMachine).start();

let previousSnapshot = encounterActor.getSnapshot();

encounterActor.subscribe((snapshot) => {
  const previous = previousSnapshot;
  previousSnapshot = snapshot;

  if (previous.value === snapshot.value) return;

  const encounterId = snapshot.context.encounterId;
  const patientId = patientStore.getState().patientId;
  if (encounterId == null || patientId == null) return;

  if (snapshot.matches("connected")) {
    events.startEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  if (snapshot.matches("in_progress")) {
    events.encounterInProgress.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  if (snapshot.matches("finalizing")) {
    events.finalizeEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
    });
  }

  if (snapshot.matches("completed")) {
    const duration_ms =
      previous.context.startedAtMs != null
        ? Math.max(0, Date.now() - previous.context.startedAtMs)
        : undefined;

    events.completeEncounter.track({
      encounter_id: encounterId,
      patient_id: patientId,
      ...(duration_ms !== undefined ? { duration_ms } : {}),
      success: true,
    });
  }
});

export function sendEncounterEvent(event: EncounterEvent): void {
  encounterActor.send(event);
}

export function matchesActiveEncounter(eventEncounterId: number): boolean {
  const active = encounterActor.getSnapshot().context.encounterId;
  return active != null && active === eventEncounterId;
}

export function registerEncounterId(id: number | undefined): void {
  sendEncounterEvent({
    type: "ENCOUNTER_ID_CHANGED",
    encounterId: PositiveInt.parse(id),
  });
}

function trackReopen(newEncounterId?: number): void {
  const encounterId = encounterActor.getSnapshot().context.encounterId;
  const patientId = patientStore.getState().patientId;
  if (encounterId == null || patientId == null) return;

  events.reopenEncounter.track({
    encounter_id: encounterId,
    patient_id: patientId,
    ...(newEncounterId != null ? { new_encounter_id: newEncounterId } : {}),
  });
}

const onTranscriptionCreated = (payload: { encounterId: number }) => {
  const encounterId = PositiveInt.parse(payload.encounterId);
  if (encounterId == null) return;
  encounterActor.send({ type: "TRANSCRIPTION_CREATED", encounterId });
};

const onEncounterSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "SNAPSHOT_RECEIVED" });
};

const onEncounterCompleted = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "COMPLETED" });
};

const onEncounterFinalizing = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "FINALIZING" });
};

const onEncounterReopened = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  trackReopen();
  encounterActor.send({ type: "REOPENED" });
};

const onTranscriptSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_SNAPSHOT_RECEIVED" });
};

const onTranscriptItemCreated = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_ITEM_CREATED" });
};

const onEncounterSupersededByReopen = (payload: {
  encounterId: number;
  newEncounterId: number;
}) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  const newEncounterId = PositiveInt.parse(payload.newEncounterId);
  if (newEncounterId == null) return;
  trackReopen(newEncounterId);
  encounterActor.send({ type: "SUPERSEDED", newEncounterId });
};

const onParamsChanged = (payload: {
  params: Record<string, string | undefined>;
}) => {
  const patientId = PositiveInt.parse(payload.params.patientId);
  const encounterId = PositiveInt.parse(payload.params.encounterId);

  const currentPatientId = patientStore.getState().patientId;
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (patientId !== currentPatientId) {
    patientStore.getState().setPatientId(patientId);
    encounterActor.send({ type: "PATIENT_CLEARED" });
  }

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

const onSearchParamsChanged = (payload: { searchParams: URLSearchParams }) => {
  const encounterId = PositiveInt.parse(
    payload.searchParams.get("encounterId"),
  );
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

appBus.on("encounter.transcription_created", onTranscriptionCreated);
appBus.on("encounter.snapshot", onEncounterSnapshot);
appBus.on("encounter.completed", onEncounterCompleted);
appBus.on("encounter.finalizing", onEncounterFinalizing);
appBus.on("encounter.reopened", onEncounterReopened);
appBus.on("transcript.snapshot", onTranscriptSnapshot);
appBus.on("transcript.item.created", onTranscriptItemCreated);
appBus.on("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
appBus.on("client.router_params_changed", onParamsChanged);
appBus.on("client.router_search_params_changed", onSearchParamsChanged);

export function __resetForTesting(): void {
  appBus.off("encounter.transcription_created", onTranscriptionCreated);
  appBus.off("encounter.snapshot", onEncounterSnapshot);
  appBus.off("encounter.completed", onEncounterCompleted);
  appBus.off("encounter.finalizing", onEncounterFinalizing);
  appBus.off("encounter.reopened", onEncounterReopened);
  appBus.off("transcript.snapshot", onTranscriptSnapshot);
  appBus.off("transcript.item.created", onTranscriptItemCreated);
  appBus.off("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
  appBus.off("client.router_params_changed", onParamsChanged);
  appBus.off("client.router_search_params_changed", onSearchParamsChanged);

  encounterActor.stop();
}
